import { Button } from "@progress/kendo-react-buttons";
import { DateTimePicker } from "@progress/kendo-react-dateinputs";
import { DropDownList } from "@progress/kendo-react-dropdowns";
import { Loader } from "@progress/kendo-react-indicators";
import { Input, NumericTextBox } from "@progress/kendo-react-inputs";
import dayjs from 'dayjs';
import { useState } from "react";
import { useHotkeys } from "react-hotkeys-hook";
import { useHistory } from 'react-router-dom';
import { fetchApi } from "services/api";
import { JsonResponse } from "TypeGen/json-response";
import { ARCCrossDockChargeType } from "TypeGen/Trip/PayTrip/arc-cross-dock-charge-type";
import CarrierAutoComplete from "views/AssetCarriers/CarrierAutoComplete";
import DriverAutoComplete from "views/AssetDrivers/DriverAutoComplete";
import VehicleAutoComplete from "views/AssetVehicles/VehicleAutoComplete";
import KendoWindow from "../../../components/KendoWindow";
import { Title } from "../../../utils/title";

type CreatePayTripProps = {
  tripId?: number | null;
  orderId?: number | null;
  isARCCrossDockTransferOrder?: boolean;
  endpoint?: string;
  closeDialog?: () => void;
};

type ARCCrossDockChargeOption = {
  ID: ARCCrossDockChargeType;
  Name: string;
};

const ARC_CARRIER_ID = 7536;
const arcCrossDockChargeOptions: ARCCrossDockChargeOption[] = [
  { ID: ARCCrossDockChargeType.ARCWarehouseOfficeHours, Name: 'ARC Warehouse - Office Hours' },
  { ID: ARCCrossDockChargeType.L1HandledAfterHoursWeekends, Name: 'L1 Handled - After Hours-Weekends' },
];

const CreatePayTrip = (props: CreatePayTripProps) => {

  const history = useHistory();
  const [saving, setSaving] = useState(false);

  // Options
  const [vehicleId, setVehicleId] = useState<number>(null);
  const [driverId, setDriverId] = useState<number>(null);
  const [carrierId, setCarrierId] = useState<number>(null);
  const [dateTime, setDateTime] = useState<Date>(dayjs().toDate());
  const [orderNumber, setOrderNumber] = useState<number>(null);
  const [crossDockChargeType, setCrossDockChargeType] = useState<ARCCrossDockChargeType>(null);
  const [skidCount, setSkidCount] = useState<number>(null);

  const save = () => {
    if (saving) return;

    if (props.isARCCrossDockTransferOrder && (!crossDockChargeType || !skidCount || !dateTime)) {
      alert('XDock charge, skids, and date/time are required.');
      return;
    }

    setSaving(true);
    const data = {
      VehicleID: vehicleId,
      DriverID: driverId,
      CarrierID: props.isARCCrossDockTransferOrder ? ARC_CARRIER_ID : carrierId,
      DateTime: dateTime,
      TripID: props.tripId,
      OrderNumber: orderNumber,
      OrderID: props.orderId,
      CrossDockChargeType: crossDockChargeType,
      SkidCount: skidCount
    }
    const endpoint = props.endpoint ?? "/api/Trip/CreatePayTrip";
    fetchApi(endpoint, data, "POST")
      .then((e: JsonResponse) => {
        if (e.Success) {
          const link = e.Links.find(l => l.Name === 'Trip');
          if (link) history.push(link.Link);
          if (props.closeDialog)
            props.closeDialog();
        } else alert(e.ErrorMessage);
      })
      .catch((e) => {
        // If not problem details
        if (!e?.status) alert('Error saving Pay Trip');
      })
      .finally(() => {
        setSaving(false);
      });
  }

  useHotkeys('ctrl+s', (e, handler) => {
    switch (handler.keys.join('')) {
      case 's':
        save();
        e.preventDefault();
        break;
    }
  }, [save]);

  const dataView = () => {
    const title = props.isARCCrossDockTransferOrder
      ? 'Create ARC Cross Dock'
      : props.closeDialog ? 'Pay Another Asset' : 'Create Additional Pay Trip';

    return (
      <div className="container">
        <Title string={title} />
        <h2 className="text-center">{title}</h2>
        <div className="card my-2">
          <div className="card-body">
            {!props.isARCCrossDockTransferOrder && <div className="form-group row">
              <label className="col-form-label form-check-label col-md-3">Vehicle</label>
              <div className="col-md-9">
                <VehicleAutoComplete
                  selectedVehicleID={vehicleId}
                  includeRecentlyRetiredVehicles
                  style={{ width: '350px' }}
                  onSelectedVehicle={(e) => {
                    setVehicleId(e?.VehicleID ?? null);
                    setCarrierId(null);
                  }}
                  formatDisplay={(e) => `${e.VehicleNumber} - ${e.Driver1Number}`}
                />
              </div>
            </div>}
            {!props.isARCCrossDockTransferOrder && <div className="form-group row">
              <label className="col-form-label form-check-label col-md-3">Driver</label>
              <div className="col-md-9">
                <DriverAutoComplete
                  selectedDriverID={driverId}
                  includeRecentlyRetiredDrivers
                  style={{ width: '350px' }}
                  onSelectedDriver={(e) => {
                    setDriverId(e?.DriverID ?? null);
                    setCarrierId(null);
                  }}
                />
              </div>
            </div>}
            <div className="form-group row">
              <label className="col-form-label form-check-label col-md-3">Carrier</label>
              <div className="col-md-9">
                {props.isARCCrossDockTransferOrder
                  ? <Input
                    ariaLabel="Carrier"
                    readOnly
                    style={{ width: '350px' }}
                    value="ARC"
                  />
                  : <CarrierAutoComplete
                    selectedCarrierID={carrierId}
                    style={{ width: '350px' }}
                    onSelectedCarrier={(e) => {
                      setCarrierId(e?.CarrierID ?? null);
                      setVehicleId(null);
                      setDriverId(null);
                    }}
                  />}
              </div>
            </div>
            {props.isARCCrossDockTransferOrder && <>
              <div className="form-group row">
                <label className="col-form-label form-check-label col-md-3">XDock charge</label>
                <div className="col-md-9">
                  <DropDownList
                    required
                    style={{ width: '350px' }}
                    data={arcCrossDockChargeOptions}
                    dataItemKey="ID"
                    textField="Name"
                    defaultItem={{ ID: null, Name: 'Select XDock charge' }}
                    value={arcCrossDockChargeOptions.find(x => x.ID === crossDockChargeType) ?? null}
                    onChange={(e) => setCrossDockChargeType(e.value?.ID ?? null)}
                  />
                </div>
              </div>
              <div className="form-group row">
                <label className="col-form-label form-check-label col-md-3">Skids</label>
                <div className="col-md-9">
                  <NumericTextBox
                    required
                    min={1}
                    step={1}
                    format="n0"
                    width={210}
                    value={skidCount}
                    onChange={(e) => setSkidCount(e.value == null ? null : Math.trunc(e.value))}
                  />
                </div>
              </div>
            </>}
            <div className="form-group row">
              <label htmlFor="startTime" className="col-form-label col-md-3">Date/Time</label>
              <div className="col-md-9">
                <DateTimePicker
                  required
                  width={210}
                  format="MM/dd/yyyy HH:mm"
                  id="startTime"
                  value={dateTime}
                  onChange={(e) => setDateTime(e.value)}
                />
              </div>
            </div>
            {props.tripId == null && !props.isARCCrossDockTransferOrder &&
              <div className="form-group row">
                <label htmlFor="startTime" className="col-form-label col-md-3">Link to (optional) Order Number:</label>
                <div className="col-md-9">
                  <Input
                    style={{ width: '210px' }}
                    type="number"
                    placeholder="Optional Order Number"
                    value={orderNumber}
                    onChange={(e) => setOrderNumber(e.value ? Number(e.value) : null)}
                  />
                </div>
              </div>}
          </div>
        </div>
        <div className="text-center">
          {saving && props.isARCCrossDockTransferOrder && (
            <div className="mb-3" role="status" aria-live="polite">
              <Loader type="converging-spinner" />              
            </div>
          )}
          <Button
            themeColor="primary"
            className="ml-2"
            disabled={saving || (props.isARCCrossDockTransferOrder && (!crossDockChargeType || !skidCount || !dateTime))}
            onClick={() => save()}
          >
            {saving ? 'Creating...' : title}
          </Button>
        </div>
      </div>
    );
  };

  //title={'Pay Another Asset'} 
  if (props.closeDialog) {
    return <KendoWindow onClose={props.closeDialog} autoSizePercentage={50}>
      {dataView()}
    </KendoWindow>
  }

  return dataView();
}

export default CreatePayTrip;
